/**
 * AdminUsers — students, donors, and admins in a single tabbed table.
 */

import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, MoreHorizontal } from "lucide-react";
import { SectionHeader, StatusBadge, AdminButton } from "./_ui";
import { USERS } from "../../data/adminMock";

const fmt$ = (n) => `$${Number(n).toLocaleString()}`;
const TABS = [
  { key: "student", label: "Students" },
  { key: "donor",   label: "Donors" },
  { key: "admin",   label: "Admins" },
];

export const AdminUsers = () => {
  const [tab, setTab] = useState("student");
  const [query, setQuery] = useState("");

  const rows = useMemo(() => USERS.filter((u) => u.role === tab).filter((u) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  }), [tab, query]);

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Users"
        title="Everyone who touches My Rich Aunt."
        description="View history, resend verification, or grant admin rights."
      />

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`users-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition ${
              tab === t.key ? "bg-ink text-sand" : "bg-sand text-ink ring-1 ring-inset ring-ink/15 hover:ring-ink/40"
            }`}
          >
            {t.label} ({USERS.filter((u) => u.role === t.key).length})
          </button>
        ))}
        <div className="ml-auto inline-flex items-center gap-2 rounded-full bg-sand px-3 py-1.5 ring-1 ring-inset ring-ink/15">
          <Search className="h-3.5 w-3.5 text-ink/50" strokeWidth={1.75} />
          <input
            type="search"
            placeholder="Search users"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-56 bg-transparent text-xs text-ink outline-none placeholder:text-ink/40"
            data-testid="users-search"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-ink/10 bg-sand">
        <table className="min-w-full text-sm">
          <thead className="bg-beige/60 text-left text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/60">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              {tab === "student" && <th className="px-5 py-3">School</th>}
              {tab === "student" && <th className="px-5 py-3">Campaigns</th>}
              {tab === "student" && <th className="px-5 py-3">Raised</th>}
              {tab === "donor" && <th className="px-5 py-3">Gifts</th>}
              {tab === "donor" && <th className="px-5 py-3">Given</th>}
              <th className="px-5 py-3">Joined</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {rows.map((u) => (
              <tr key={u.id} className="hover:bg-ink/[0.03]">
                <td className="px-5 py-3 font-medium text-ink">{u.name}</td>
                <td className="px-5 py-3 text-ink/80">{u.email}</td>
                {tab === "student" && <td className="px-5 py-3 text-ink/80">{u.school}</td>}
                {tab === "student" && <td className="px-5 py-3 text-ink/80">{u.campaigns}</td>}
                {tab === "student" && <td className="px-5 py-3 text-ink/80">{fmt$(u.raised)}</td>}
                {tab === "donor" && <td className="px-5 py-3 text-ink/80">{u.gifts}</td>}
                {tab === "donor" && <td className="px-5 py-3 text-ink/80">{fmt$(u.given)}</td>}
                <td className="px-5 py-3 text-ink/60">{u.joined}</td>
                <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                <td className="px-5 py-3 text-right">
                  <AdminButton
                    size="sm"
                    variant="secondary"
                    onClick={() => toast(`View ${u.name}'s history`)}
                    testid={`view-${u.id}`}
                  >
                    <MoreHorizontal className="h-3 w-3" strokeWidth={1.75} /> View
                  </AdminButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
